import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { PageMeta } from '../components/seo/PageMeta'
import { SectionLabel } from '../components/ui/SectionLabel'
import { api } from '../lib/api'
import { pageMeta } from '../lib/seo'
import { blogListSchema } from '../lib/schema'

const CATEGORIES = ['All', 'AI & Automation', 'Cybersecurity', 'Managed IT', 'Tech Explainers', 'Client Stories']

const CATEGORY_SLUG_MAP: Record<string, string> = {
  'All': '',
  'AI & Automation': 'ai-automation',
  'Cybersecurity': 'cybersecurity',
  'Managed IT': 'managed-it',
  'Tech Explainers': 'tech-explainers',
  'Client Stories': 'client-stories',
}

const DISPLAY_CATEGORY_MAP: Record<string, string> = {
  'from-the-team': 'Client Stories',
  'ai-automation': 'AI & Automation',
  'cybersecurity': 'Cybersecurity',
  'managed-it': 'Managed IT',
  'tech-explainers': 'Tech Explainers',
  'client-stories': 'Client Stories',
}

interface Post {
  id: string
  title: string
  slug: string
  excerpt: string
  featured_image_url: string | null
  category: string
  author_name: string
  published_at: string | null
}

export default function Blog() {
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [category, setCategory] = useState('All')
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const limit = 9

  useEffect(() => {
    setLoading(true)
    setOffset(0)
    const slug = CATEGORY_SLUG_MAP[category] || ''
    api
      .get('/api/blog/posts', { params: { category: slug || undefined, limit, offset: 0 } })
      .then((res) => {
        setPosts(res.data)
        setHasMore(res.data.length === limit)
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false))
  }, [category])

  const loadMore = () => {
    setLoadingMore(true)
    const newOffset = offset + limit
    const slug = CATEGORY_SLUG_MAP[category] || ''
    api
      .get('/api/blog/posts', { params: { category: slug || undefined, limit, offset: newOffset } })
      .then((res) => {
        setPosts((p) => [...p, ...res.data])
        setHasMore(res.data.length === limit)
        setOffset(newOffset)
      })
      .finally(() => setLoadingMore(false))
  }

  const displayCategory = (cat: string) => DISPLAY_CATEGORY_MAP[cat] || cat

  return (
    <>
      <PageMeta
        title={pageMeta.blog.title}
        description={pageMeta.blog.description}
        canonical={pageMeta.blog.canonical}
        schema={blogListSchema}
      />
      <main className="min-h-screen pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="font-display font-black text-4xl md:text-5xl uppercase text-white mb-12 text-center">
            IT & AI Insights for Pacific Northwest Businesses
          </h1>

          <div className="flex flex-wrap gap-2 justify-center mb-12">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-lg font-body text-sm transition-colors ${
                  category === c ? 'bg-accent text-white' : 'bg-bg-3 text-cream-dim hover:text-white'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-64 rounded-lg animate-pulse"
                  style={{ backgroundColor: 'var(--bg-3)' }}
                />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center font-body text-cream-dim py-16">
              No posts yet — check back soon.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="block p-0 rounded-lg overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1"
                    style={{ backgroundColor: 'var(--bg-3)' }}
                  >
                    <div
                      className="aspect-video w-full"
                      style={{
                        backgroundColor: 'var(--bg-4)',
                        backgroundImage: post.featured_image_url ? `url(${post.featured_image_url})` : undefined,
                        backgroundSize: 'cover',
                      }}
                    />
                    <div className="p-6">
                      <SectionLabel className="mb-2">
                        {displayCategory(post.category)}
                      </SectionLabel>
                      <h2 className="font-display font-bold text-xl text-white mb-2 line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="font-body text-cream-dim text-base line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>
                      <p className="font-body text-cream-faint text-xs">
                        {post.author_name} · {post.published_at ? new Date(post.published_at).toLocaleDateString('en-US') : ''}
                      </p>
                      <span className="font-body text-sm mt-2 inline-block" style={{ color: 'var(--accent)' }}>
                        Read More →
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
              {hasMore && (
                <div className="text-center mt-12">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-8 py-3 rounded-lg font-body font-medium bg-accent text-white disabled:opacity-50"
                  >
                    {loadingMore ? 'Loading...' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  )
}
export { Blog as Component }
